import { useCallback, useMemo, useRef, useState } from 'react'
import type { Coordinate, Problem, UnitData, UnitState } from '../types'
import { BANK_COLS, BANK_EMPTY_CELL, BANK_ROWS, MAP_SIZE } from '../constants'

function createMapGrid(): UnitData[][] {
  const grid: UnitData[][] = []
  for (let col = 0; col < MAP_SIZE; col++) {
    grid[col] = []
    for (let row = 0; row < MAP_SIZE; row++) {
      grid[col][row] = { col, row, state: 'off', container: 'map' }
    }
  }
  return grid
}

function createBankGrid(): UnitData[][] {
  const grid: UnitData[][] = []
  for (let col = 0; col < BANK_COLS; col++) {
    grid[col] = []
    for (let row = 0; row < BANK_ROWS; row++) {
      const isEmpty = col === BANK_EMPTY_CELL.col && row === BANK_EMPTY_CELL.row
      grid[col][row] = {
        col,
        row,
        state: isEmpty ? 'off' : 'on',
        container: 'bank',
        isEmpty,
      }
    }
  }
  return grid
}

function cloneGrid(grid: UnitData[][]): UnitData[][] {
  return grid.map(col => col.map(cell => ({ ...cell })))
}

export function useGridState() {
  const [mapGrid, setMapGrid] = useState<UnitData[][]>(createMapGrid)
  const [bankGrid, setBankGrid] = useState<UnitData[][]>(createBankGrid)

  // Refs for always-current state (moveSelectedTo에서 stale closure 방지)
  const mapGridRef = useRef(mapGrid)
  mapGridRef.current = mapGrid
  const bankGridRef = useRef(bankGrid)
  bankGridRef.current = bankGrid

  /** 수식에 맞게 맵에 인형 배치 (left=행 수, right=열 수) */
  const showProblem = useCallback((problem: Problem) => {
    setMapGrid(prev => {
      const next = cloneGrid(prev)
      // 먼저 전부 off
      for (let col = 0; col < MAP_SIZE; col++) {
        for (let row = 0; row < MAP_SIZE; row++) {
          next[col][row].state = 'off'
        }
      }
      // problem.right 열, problem.left 행만큼 on
      for (let col = 0; col < problem.right && col < MAP_SIZE; col++) {
        for (let row = 0; row < problem.left && row < MAP_SIZE; row++) {
          next[col][row].state = 'on'
        }
      }
      return next
    })
    // 은행 리셋
    setBankGrid(createBankGrid)
  }, [])

  /** 특정 셀 상태 변경 */
  const setUnitState = useCallback(
    (container: 'map' | 'bank', col: number, row: number, state: UnitState) => {
      const setter = container === 'map' ? setMapGrid : setBankGrid
      setter(prev => {
        const next = cloneGrid(prev)
        next[col][row].state = state
        return next
      })
    },
    [],
  )

  /** 범위 내 인형 상태 일괄 변경 */
  const setRangeState = useCallback(
    (
      container: 'map' | 'bank',
      startCol: number,
      startRow: number,
      endCol: number,
      endRow: number,
      state: UnitState,
    ) => {
      const setter = container === 'map' ? setMapGrid : setBankGrid
      setter(prev => {
        const next = cloneGrid(prev)
        for (let col = startCol; col <= endCol; col++) {
          for (let row = startRow; row <= endRow; row++) {
            if (next[col]?.[row] && !next[col][row].isEmpty) {
              next[col][row].state = state
            }
          }
        }
        return next
      })
    },
    [],
  )

  /** 범위 내 인형 토글 (on<->off) */
  const toggleRange = useCallback(
    (startCol: number, startRow: number, endCol: number, endRow: number) => {
      setMapGrid(prev => {
        const next = cloneGrid(prev)
        for (let col = startCol; col <= endCol; col++) {
          for (let row = startRow; row <= endRow; row++) {
            if (next[col]?.[row]) {
              next[col][row].state = next[col][row].state === 'on' ? 'off' : 'on'
            }
          }
        }
        return next
      })
    },
    [],
  )

  /** 범위 내 select 인형을 off로, 대상 범위 내 off 칸을 on으로 (개수 일치 시) */
  const moveUnitsRange = useCallback(
    (destStartCol: number, destStartRow: number, destEndCol: number, destEndRow: number): boolean => {
      let moved = false
      setMapGrid(prev => {
        const next = cloneGrid(prev)

        // 선택된 인형 좌표 수집
        const selected: Coordinate[] = []
        for (let c = 0; c < MAP_SIZE; c++) {
          for (let r = 0; r < MAP_SIZE; r++) {
            if (next[c][r].state === 'select') selected.push({ col: c, row: r })
          }
        }
        if (selected.length === 0) return prev

        // 대상 범위 내 off 칸 수집 (on/select/done 칸 제외)
        const destOff: Coordinate[] = []
        for (let c = destStartCol; c <= destEndCol; c++) {
          for (let r = destStartRow; r <= destEndRow; r++) {
            if (next[c]?.[r]?.state === 'off') destOff.push({ col: c, row: r })
          }
        }

        // 개수 불일치 → 취소
        if (destOff.length !== selected.length) return prev

        // 대상에 기존 인형(on/select/done)이 겹치는지 확인
        for (let c = destStartCol; c <= destEndCol; c++) {
          for (let r = destStartRow; r <= destEndRow; r++) {
            const st = next[c]?.[r]?.state
            if (st === 'on' || st === 'done') return prev // 겹침 → 취소
          }
        }

        // 이동 실행: select → off, destOff → on
        for (const s of selected) {
          next[s.col][s.row].state = 'off'
        }
        for (const d of destOff) {
          next[d.col][d.row].state = 'on'
        }
        moved = true
        return next
      })
      return moved
    },
    [],
  )

  /** 은행 범위 내 on 인형을 select로 (grab용). 선택된 개수 반환 */
  const selectBankRange = useCallback(
    (startCol: number, startRow: number, endCol: number, endRow: number): number => {
      let count = 0
      setBankGrid(prev => {
        const next = cloneGrid(prev)
        for (let c = startCol; c <= endCol; c++) {
          for (let r = startRow; r <= endRow; r++) {
            if (next[c]?.[r] && !next[c][r].isEmpty && next[c][r].state === 'on') {
              next[c][r].state = 'select'
              count++
            }
          }
        }
        return next
      })
      return count
    },
    [],
  )

  /** 크로스 컨테이너 이동: 양쪽 그리드에서 select를 찾아 대상 컨테이너의 off 칸으로 이동 */
  const moveSelectedTo = useCallback(
    (
      destContainer: 'map' | 'bank',
      destStartCol: number,
      destStartRow: number,
      destEndCol: number,
      destEndRow: number,
    ): boolean => {
      // ref에서 최신 상태 읽기 (stale closure 방지)
      const mapSnap = cloneGrid(mapGridRef.current)
      const bankSnap = cloneGrid(bankGridRef.current)

      // 양쪽에서 select 수집
      const mapSelected: Coordinate[] = []
      for (let c = 0; c < MAP_SIZE; c++) {
        for (let r = 0; r < MAP_SIZE; r++) {
          if (mapSnap[c][r].state === 'select') mapSelected.push({ col: c, row: r })
        }
      }
      const bankSelected: Coordinate[] = []
      for (let c = 0; c < BANK_COLS; c++) {
        for (let r = 0; r < BANK_ROWS; r++) {
          if (bankSnap[c][r].state === 'select') bankSelected.push({ col: c, row: r })
        }
      }
      const totalSelected = mapSelected.length + bankSelected.length
      if (totalSelected === 0) return false

      // 대상 범위 내 off 칸 수집
      const destGrid = destContainer === 'map' ? mapSnap : bankSnap
      const maxCol = destContainer === 'map' ? MAP_SIZE : BANK_COLS
      const maxRow = destContainer === 'map' ? MAP_SIZE : BANK_ROWS
      const destOff: Coordinate[] = []
      for (let c = destStartCol; c <= Math.min(destEndCol, maxCol - 1); c++) {
        for (let r = destStartRow; r <= Math.min(destEndRow, maxRow - 1); r++) {
          if (destGrid[c]?.[r]?.isEmpty) continue
          if (destGrid[c]?.[r]?.state === 'off') destOff.push({ col: c, row: r })
        }
      }

      // 선택 인형 수 = 대상 범위 내 off 칸 수 일치해야 배치 가능
      if (destOff.length !== totalSelected) return false

      // 실행: source select → off
      if (mapSelected.length > 0) {
        setMapGrid(prev => {
          const next = cloneGrid(prev)
          for (const s of mapSelected) next[s.col][s.row].state = 'off'
          return next
        })
      }
      if (bankSelected.length > 0) {
        setBankGrid(prev => {
          const next = cloneGrid(prev)
          for (const s of bankSelected) next[s.col][s.row].state = 'off'
          return next
        })
      }

      // 실행: dest off → on
      const destSetter = destContainer === 'map' ? setMapGrid : setBankGrid
      destSetter(prev => {
        const next = cloneGrid(prev)
        for (const d of destOff) next[d.col][d.row].state = 'on'
        return next
      })

      return true
    },
    [], // ref에서 최신 상태를 읽으므로 deps 불필요
  )

  /** select 상태 인형을 모두 on으로 되돌리기 (양쪽 컨테이너) */
  const cancelSelection = useCallback(() => {
    setMapGrid(prev => {
      const next = cloneGrid(prev)
      for (let c = 0; c < MAP_SIZE; c++) {
        for (let r = 0; r < MAP_SIZE; r++) {
          if (next[c][r].state === 'select') next[c][r].state = 'on'
        }
      }
      return next
    })
    setBankGrid(prev => {
      const next = cloneGrid(prev)
      for (let c = 0; c < BANK_COLS; c++) {
        for (let r = 0; r < BANK_ROWS; r++) {
          if (next[c][r].state === 'select') next[c][r].state = 'on'
        }
      }
      return next
    })
  }, [])

  /** 맵에서 on 상태인 낱개(done이 아닌) 인형 좌표 목록 */
  const looseUnits = useMemo(() => {
    const units: Coordinate[] = []
    for (let col = 0; col < MAP_SIZE; col++) {
      for (let row = 0; row < MAP_SIZE; row++) {
        if (mapGrid[col][row].state === 'on') {
          units.push({ col, row })
        }
      }
    }
    return units
  }, [mapGrid])

  /** 맵에서 done 상태인 셀 개수 (묶인 인형 수) */
  const doneCount = useMemo(() => {
    let count = 0
    for (let col = 0; col < MAP_SIZE; col++) {
      for (let row = 0; row < MAP_SIZE; row++) {
        if (mapGrid[col][row].state === 'done') count++
      }
    }
    return count
  }, [mapGrid])

  /** 은행이 가득 찼는지 확인 (select, off 모두 비어있는 것으로 간주) */
  const isBankFull = useMemo(() => {
    for (let col = 0; col < BANK_COLS; col++) {
      for (let row = 0; row < BANK_ROWS; row++) {
        if (bankGrid[col][row].isEmpty) continue
        const st = bankGrid[col][row].state
        if (st === 'off' || st === 'select') return false
      }
    }
    return true
  }, [bankGrid])

  /** 은행 빈 칸(off, non-isEmpty) 수 */
  const bankBorrowedCount = useMemo(() => {
    let count = 0
    for (let col = 0; col < BANK_COLS; col++) {
      for (let row = 0; row < BANK_ROWS; row++) {
        if (bankGrid[col][row].isEmpty) continue
        if (bankGrid[col][row].state === 'off') count++
      }
    }
    return count
  }, [bankGrid])

  /** 은행에서 빌리기: 범위 내 'on' 셀을 'select'로 (선택 표시). 변경된 수 반환 */
  const borrowFromBank = useCallback(
    (startCol: number, startRow: number, endCol: number, endRow: number): number => {
      // ref에서 현재 상태 읽어 count 먼저 계산 (setState updater는 비동기)
      const current = bankGridRef.current
      let count = 0
      for (let c = startCol; c <= endCol; c++) {
        for (let r = startRow; r <= endRow; r++) {
          if (current[c]?.[r] && !current[c][r].isEmpty && current[c][r].state === 'on') count++
        }
      }
      if (count > 0) {
        setBankGrid(prev => {
          const next = cloneGrid(prev)
          for (let c = startCol; c <= endCol; c++) {
            for (let r = startRow; r <= endRow; r++) {
              if (next[c]?.[r] && !next[c][r].isEmpty && next[c][r].state === 'on') {
                next[c][r].state = 'select'
              }
            }
          }
          return next
        })
      }
      return count
    },
    [],
  )

  /** 은행 빌리기 확정: 'select' 셀을 'off'로 (맵에 놓은 후 호출). 확정된 수 반환 */
  const confirmBorrow = useCallback((maxCount: number): number => {
    const current = bankGridRef.current
    let count = 0
    for (let c = 0; c < BANK_COLS && count < maxCount; c++) {
      for (let r = 0; r < BANK_ROWS && count < maxCount; r++) {
        if (!current[c][r].isEmpty && current[c][r].state === 'select') count++
      }
    }
    const toConfirm = Math.min(count, maxCount)
    if (toConfirm > 0) {
      setBankGrid(prev => {
        const next = cloneGrid(prev)
        let confirmed = 0
        for (let c = 0; c < BANK_COLS && confirmed < toConfirm; c++) {
          for (let r = 0; r < BANK_ROWS && confirmed < toConfirm; r++) {
            if (!next[c][r].isEmpty && next[c][r].state === 'select') {
              next[c][r].state = 'off'
              confirmed++
            }
          }
        }
        return next
      })
    }
    return toConfirm
  }, [])

  /** 은행에 반납: 'off' 셀을 'on'으로 (최대 maxCount개). 반납된 수 반환 */
  const returnToBank = useCallback((maxCount: number): number => {
    const current = bankGridRef.current
    let count = 0
    for (let c = 0; c < BANK_COLS && count < maxCount; c++) {
      for (let r = 0; r < BANK_ROWS && count < maxCount; r++) {
        if (!current[c][r].isEmpty && current[c][r].state === 'off') count++
      }
    }
    const toReturn = Math.min(count, maxCount)
    if (toReturn > 0) {
      setBankGrid(prev => {
        const next = cloneGrid(prev)
        let returned = 0
        for (let c = 0; c < BANK_COLS && returned < toReturn; c++) {
          for (let r = 0; r < BANK_ROWS && returned < toReturn; r++) {
            if (!next[c][r].isEmpty && next[c][r].state === 'off') {
              next[c][r].state = 'on'
              returned++
            }
          }
        }
        return next
      })
    }
    return toReturn
  }, [])

  /** 맵에 놓기: 범위 내 'off' 셀을 'on'으로 (최대 maxCount개). 놓은 수 반환 */
  const placeOnMap = useCallback(
    (startCol: number, startRow: number, endCol: number, endRow: number, maxCount: number): number => {
      const current = mapGridRef.current
      let count = 0
      for (let c = startCol; c <= endCol && count < maxCount; c++) {
        for (let r = startRow; r <= endRow && count < maxCount; r++) {
          if (current[c]?.[r]?.state === 'off') count++
        }
      }
      const toPlace = Math.min(count, maxCount)
      if (toPlace > 0) {
        setMapGrid(prev => {
          const next = cloneGrid(prev)
          let placed = 0
          for (let c = startCol; c <= endCol && placed < toPlace; c++) {
            for (let r = startRow; r <= endRow && placed < toPlace; r++) {
              if (next[c]?.[r]?.state === 'off') {
                next[c][r].state = 'on'
                placed++
              }
            }
          }
          return next
        })
      }
      return toPlace
    },
    [],
  )

  /** 맵에서 거두기: 범위 내 'on' 셀을 'off'로 (최대 maxCount개). 거둔 수 반환 */
  const collectFromMap = useCallback(
    (startCol: number, startRow: number, endCol: number, endRow: number, maxCount: number): number => {
      const current = mapGridRef.current
      let count = 0
      for (let c = startCol; c <= endCol && count < maxCount; c++) {
        for (let r = startRow; r <= endRow && count < maxCount; r++) {
          if (current[c]?.[r]?.state === 'on') count++
        }
      }
      const toCollect = Math.min(count, maxCount)
      if (toCollect > 0) {
        setMapGrid(prev => {
          const next = cloneGrid(prev)
          let collected = 0
          for (let c = startCol; c <= endCol && collected < toCollect; c++) {
            for (let r = startRow; r <= endRow && collected < toCollect; r++) {
              if (next[c]?.[r]?.state === 'on') {
                next[c][r].state = 'off'
                collected++
              }
            }
          }
          return next
        })
      }
      return toCollect
    },
    [],
  )

  /** 뚱십 풀기: 범위 내 'done' 셀을 'on'으로 복원 */
  const untieMergedRange = useCallback(
    (startCol: number, startRow: number, endCol: number, endRow: number) => {
      setMapGrid(prev => {
        const next = cloneGrid(prev)
        for (let col = startCol; col <= endCol; col++) {
          for (let row = startRow; row <= endRow; row++) {
            if (next[col]?.[row]?.state === 'done') {
              next[col][row].state = 'on'
            }
          }
        }
        return next
      })
    },
    [],
  )

  /** 전체 리셋 */
  const reset = useCallback(() => {
    setMapGrid(createMapGrid())
    setBankGrid(createBankGrid())
  }, [])

  /** mapGrid setState 직접 접근 (useTeleport용) */
  const setMapGridDirect = setMapGrid

  return {
    mapGrid,
    bankGrid,
    showProblem,
    setUnitState,
    setRangeState,
    toggleRange,
    moveUnitsRange,
    selectBankRange,
    moveSelectedTo,
    cancelSelection,
    looseUnits,
    doneCount,
    isBankFull,
    bankBorrowedCount,
    borrowFromBank,
    confirmBorrow,
    returnToBank,
    placeOnMap,
    collectFromMap,
    untieMergedRange,
    reset,
    setMapGridDirect,
  }
}
